import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box, Avatar, Typography, Tooltip, Divider, Chip,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import ReviewsIcon from '@mui/icons-material/Reviews';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupIcon from '@mui/icons-material/Group';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MovdLogo from './MovdLogo.jsx';

// ─── nav structure ─────────────────────────────────────────────────────────────
const NAV = [
  {
    section: 'Main',
    items: [
      { label: 'Dashboard',  path: '/mover-portal/dashboard',  icon: <DashboardIcon sx={{ fontSize: 20 }} /> },
      { label: 'Bookings',   path: '/mover-portal/bookings',   icon: <AssignmentIcon sx={{ fontSize: 20 }} /> },
      { label: 'Calendar',   path: '/mover-portal/calendar',   icon: <CalendarTodayIcon sx={{ fontSize: 20 }} /> },
    ],
  },
  {
    section: 'Business',
    items: [
      { label: 'Profile',    path: '/mover-portal/profile',    icon: <BusinessIcon sx={{ fontSize: 20 }} /> },
      { label: 'Pricing',    path: '/mover-portal/pricing',    icon: <PriceChangeIcon sx={{ fontSize: 20 }} /> },
      { label: 'Reviews',    path: '/mover-portal/reviews',    icon: <ReviewsIcon sx={{ fontSize: 20 }} /> },
      { label: 'Team',       path: '/mover-portal/team',       icon: <GroupIcon sx={{ fontSize: 20 }} /> },
    ],
  },
  {
    section: 'Financials',
    items: [
      { label: 'Earnings & Escrow', path: '/mover-portal/financials', icon: <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />, badge: 'New' },
    ],
  },
];

// ─── single nav item ───────────────────────────────────────────────────────────
function NavItem({ item, active, collapsed, onNavigate }) {
  const base = {
    display: 'flex', alignItems: 'center', gap: 1.5,
    px: collapsed ? 1.5 : 2, py: 1.2,
    borderRadius: 2, cursor: 'pointer',
    textDecoration: 'none', color: 'inherit',
    transition: 'all .15s',
    position: 'relative',
    bgcolor: active ? 'rgba(37,99,235,0.18)' : 'transparent',
    '&:hover': { bgcolor: active ? 'rgba(37,99,235,0.24)' : 'rgba(255,255,255,0.06)' },
  };

  const inner = (
    <Box component={Link} to={item.path} onClick={onNavigate} sx={base}>
      {/* active indicator */}
      {active && (
        <Box sx={{
          position: 'absolute', left: 0, top: '20%', bottom: '20%',
          width: 3, borderRadius: '0 3px 3px 0', bgcolor: '#60A5FA',
        }} />
      )}
      <Box sx={{ color: active ? '#60A5FA' : '#94a3b8', flexShrink: 0, display: 'flex' }}>
        {item.icon}
      </Box>
      {!collapsed && (
        <>
          <Typography
            variant="body2"
            fontWeight={active ? 700 : 500}
            sx={{ color: active ? '#fff' : '#cbd5e1', flex: 1, fontSize: 13.5 }}
          >
            {item.label}
          </Typography>
          {item.badge && (
            <Chip label={item.badge} size="small" sx={{
              height: 18, fontSize: 10, fontWeight: 700,
              bgcolor: '#2563EB', color: '#fff',
            }} />
          )}
        </>
      )}
    </Box>
  );

  return collapsed ? (
    <Tooltip title={item.label} placement="right" arrow>{inner}</Tooltip>
  ) : inner;
}

// ─── main sidebar ──────────────────────────────────────────────────────────────
export default function MoverSidebar({ profile, onLogout, onNavigate, showCollapse = true }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const W = collapsed ? 72 : 230;

  const handleNavClick = () => {
    onNavigate?.();
  };

  return (
    <Box sx={{
      width: W, flexShrink: 0,
      height: '100vh',
      bgcolor: '#1E293B',
      display: 'flex', flexDirection: 'column',
      borderRight: '1px solid rgba(255,255,255,0.08)',
      transition: 'width .2s',
      overflow: 'hidden',
      position: 'sticky',
      top: 0,
      zIndex: 1200,
    }}>

      {/* ── Logo / brand ───────────────────────────────── */}
      <Box sx={{
        px: collapsed ? 1.5 : 2.5, py: 2.5,
        display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}>
        <MovdLogo size="small" variant="full" color="white" sx={{ flexShrink: 0, maxWidth: collapsed ? 42 : 96, overflow: 'hidden' }} />
        {!collapsed && (
          <Box>
            <Typography variant="caption" sx={{ color: '#CBD5E1', fontSize: 10, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase' }}>Partner Portal</Typography>
          </Box>
        )}
      </Box>

      {/* ── Profile strip ──────────────────────────────── */}
      <Box sx={{
        px: collapsed ? 1 : 2, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}>
        <Avatar
          src={profile?.profileImage}
          sx={{ width: 34, height: 34, bgcolor: '#1e3a5f', fontWeight: 700, fontSize: 15, flexShrink: 0 }}
        >
          {profile?.name?.[0] ?? 'M'}
        </Avatar>
        {!collapsed && (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: '#e2e8f0', fontSize: 13 }} noWrap>
              {profile?.name || 'Mover Company'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#93C5FD', fontSize: 11 }}>Active partner</Typography>
          </Box>
        )}
      </Box>

      {/* ── Nav sections ───────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1.5, px: collapsed ? 1 : 1.5,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#1e293b', borderRadius: 2 },
      }}>
        {NAV.map((group) => (
          <Box key={group.section} sx={{ mb: 2 }}>
            {!collapsed && (
              <Typography variant="caption" sx={{
                color: '#475569', fontWeight: 700,
                letterSpacing: 1.2, textTransform: 'uppercase', fontSize: 10,
                px: 1, mb: 0.5, display: 'block',
              }}>
                {group.section}
              </Typography>
            )}
            {group.items.map(item => (
              <NavItem
                key={item.path}
                item={item}
                active={location.pathname === item.path || location.pathname + location.hash === item.path}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </Box>
        ))}
      </Box>

      {/* ── Bottom: collapse toggle + settings + logout ─ */}
      <Box sx={{ px: collapsed ? 1 : 1.5, py: 1.5, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <NavItem
          item={{ label: 'Settings', path: '/mover-portal/settings', icon: <SettingsIcon sx={{ fontSize: 20 }} /> }}
          active={location.pathname === '/mover-portal/settings'}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />

        {/* Logout */}
        <Box
          onClick={() => { onNavigate?.(); onLogout?.(); }}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: collapsed ? 1.5 : 2, py: 1.2,
            borderRadius: 2, cursor: 'pointer',
            color: '#94a3b8',
            '&:hover': { bgcolor: 'rgba(239,68,68,0.1)', color: '#ef4444' },
            transition: 'all .15s',
          }}
        >
          <LogoutIcon sx={{ fontSize: 20, flexShrink: 0 }} />
          {!collapsed && <Typography variant="body2" fontWeight={500} sx={{ fontSize: 13.5 }}>Log Out</Typography>}
        </Box>

        {/* Collapse toggle */}
        {showCollapse && (
          <Box
            onClick={() => setCollapsed(c => !c)}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-end',
              px: 1, py: 0.5, cursor: 'pointer', borderRadius: 2,
              color: '#475569', '&:hover': { color: '#94a3b8' },
              transition: 'all .15s',
            }}
          >
            {collapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
          </Box>
        )}
      </Box>
    </Box>
  );
}
